import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, ChevronDown, Save, Target, X } from 'lucide-react';
import { listGoals, createGoal, updateGoal, deleteGoal } from '../../lib/api';
import { queryKeys } from '../../lib/queryKeys';
import type { Goal, KeyResult, KrStatus } from '../../types';

const KR_STATUS_LABELS: Record<KrStatus, string> = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  at_risk: 'At Risk',
  achieved: 'Achieved',
};

const KR_STATUS_COLORS: Record<KrStatus, string> = {
  not_started: 'text-gray-400',
  in_progress: 'text-blue-500',
  at_risk: 'text-orange-500',
  achieved: 'text-green-500',
};

function ProgressBar({ value }: { value: number }) {
  const color = value >= 100 ? 'bg-green-500' : value >= 60 ? 'bg-blue-500' : value >= 30 ? 'bg-amber-400' : 'bg-gray-300 dark:bg-white/10';
  return (
    <div className="h-1.5 w-full bg-gray-100 dark:bg-white/10 rounded-full overflow-hidden">
      <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${value}%` }} />
    </div>
  );
}

function emptyKr(): KeyResult {
  return { krId: '', title: '', metric: null, progress: 0, status: 'not_started' };
}

function GoalRow({ goal, onSaved }: { goal: Goal; onSaved: () => void }) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Goal>(goal);

  const saveMutation = useMutation({
    mutationFn: () => updateGoal(goal.goalId, form),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: queryKeys.goals }); setEditing(false); onSaved(); },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteGoal(goal.goalId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.goals }),
  });

  const avgProgress = form.keyResults.length
    ? Math.round(form.keyResults.reduce((s, kr) => s + kr.progress, 0) / form.keyResults.length)
    : 0;

  const addKr = () => setForm((p) => ({ ...p, keyResults: [...p.keyResults, emptyKr()] }));
  const removeKr = (i: number) => setForm((p) => ({ ...p, keyResults: p.keyResults.filter((_, idx) => idx !== i) }));
  const updateKr = (i: number, patch: Partial<KeyResult>) =>
    setForm((p) => ({ ...p, keyResults: p.keyResults.map((kr, idx) => idx === i ? { ...kr, ...patch } : kr) }));

  return (
    <div className="border border-gray-100 dark:border-white/[0.07] rounded-xl overflow-hidden">
      <div
        className="flex items-center gap-3 px-4 py-4 bg-white dark:bg-white/[0.02] cursor-pointer hover:bg-gray-50 dark:hover:bg-white/[0.04] transition-colors"
        onClick={() => { setExpanded((p) => !p); if (!expanded) setEditing(false); }}
      >
        <ChevronDown className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-title text-gray-900 dark:text-white truncate">{goal.title}</p>
            {goal.timeframe && (
              <span className="text-micro px-1.5 py-0.5 rounded bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-400 flex-shrink-0">
                {goal.timeframe}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-1">
            <ProgressBar value={avgProgress} />
            <span className="text-meta text-gray-500 flex-shrink-0">{avgProgress}%</span>
          </div>
        </div>
        <span className="text-meta text-gray-400 flex-shrink-0">{goal.keyResults.length} KR{goal.keyResults.length !== 1 ? 's' : ''}</span>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-2 bg-gray-50 dark:bg-white/[0.01] space-y-4">
              {!editing ? (
                <>
                  {goal.description && (
                    <p className="text-meta text-gray-500 dark:text-gray-400 max-w-2xl">{goal.description}</p>
                  )}
                  <div className="space-y-4">
                    {goal.keyResults.map((kr) => (
                      <div key={kr.krId} className="flex items-center gap-3 bg-white dark:bg-white/[0.03] rounded-lg px-3 py-4 border border-gray-100 dark:border-white/[0.06]">
                        <div className="flex-1 min-w-0">
                          <p className="text-body text-gray-800 dark:text-gray-200">{kr.title}</p>
                          {kr.metric && <p className="text-meta text-gray-400 mt-0.5">{kr.metric}</p>}
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <div className="w-20">
                            <ProgressBar value={kr.progress} />
                          </div>
                          <span className="text-meta w-8 text-right text-gray-500">{kr.progress}%</span>
                          <span className={`text-meta font-medium ${KR_STATUS_COLORS[kr.status]}`}>
                            {KR_STATUS_LABELS[kr.status]}
                          </span>
                        </div>
                      </div>
                    ))}
                    {goal.keyResults.length === 0 && (
                      <p className="text-meta text-gray-400 dark:text-gray-500 italic">No key results yet.</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditing(true); setForm(goal); }}
                      className="text-meta px-3 py-1.5 rounded-lg border border-primary/30 text-primary hover:bg-primary/10 transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); if (confirm('Delete this goal?')) deleteMutation.mutate(); }}
                      className="text-meta px-3 py-1.5 rounded-lg border border-red-200 dark:border-red-900/30 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </>
              ) : (
                <div className="space-y-4" onClick={(e) => e.stopPropagation()}>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <label className="block text-meta text-gray-500 mb-1">Objective title</label>
                      <input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                        className="w-full px-3 py-2 rounded-lg bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-body text-gray-900 dark:text-white focus:outline-none focus:border-primary/50" />
                    </div>
                    <div>
                      <label className="block text-meta text-gray-500 mb-1">Timeframe</label>
                      <input value={form.timeframe ?? ''} placeholder="e.g. Q2 2026"
                        onChange={(e) => setForm((p) => ({ ...p, timeframe: e.target.value || null }))}
                        className="w-full px-3 py-2 rounded-lg bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-body text-gray-900 dark:text-white focus:outline-none focus:border-primary/50" />
                    </div>
                    <div>
                      <label className="block text-meta text-gray-500 mb-1">Owner</label>
                      <input value={form.ownerName ?? ''} placeholder="Owner name"
                        onChange={(e) => setForm((p) => ({ ...p, ownerName: e.target.value || null }))}
                        className="w-full px-3 py-2 rounded-lg bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-body text-gray-900 dark:text-white focus:outline-none focus:border-primary/50" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-meta text-gray-500 mb-1">Description</label>
                      <textarea value={form.description ?? ''} rows={2}
                        onChange={(e) => setForm((p) => ({ ...p, description: e.target.value || null }))}
                        className="w-full px-3 py-2 rounded-lg bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-body text-gray-900 dark:text-white focus:outline-none focus:border-primary/50 resize-none" />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-meta font-medium text-gray-700 dark:text-gray-300">Key Results</p>
                      <button onClick={addKr} className="text-meta text-primary hover:text-primary/80 flex items-center gap-1">
                        <Plus className="h-4 w-4" /> Add KR
                      </button>
                    </div>
                    <div className="space-y-4">
                      {form.keyResults.map((kr, i) => (
                        <div key={i} className="flex items-start gap-2 bg-white dark:bg-white/[0.03] rounded-lg px-3 py-2 border border-gray-100 dark:border-white/[0.06]">
                          <div className="flex-1 grid grid-cols-2 gap-2">
                            <input value={kr.title} placeholder="Key result title"
                              onChange={(e) => updateKr(i, { title: e.target.value })}
                              className="col-span-2 px-2 py-1.5 rounded bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-meta text-gray-900 dark:text-white focus:outline-none focus:border-primary/50" />
                            <input value={kr.metric ?? ''} placeholder="Metric (optional)"
                              onChange={(e) => updateKr(i, { metric: e.target.value || null })}
                              className="px-2 py-1.5 rounded bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-meta text-gray-900 dark:text-white focus:outline-none focus:border-primary/50" />
                            <div className="flex gap-2">
                              <input type="number" min={0} max={100} value={kr.progress}
                                onChange={(e) => updateKr(i, { progress: Math.max(0, Math.min(100, parseInt(e.target.value) || 0)) })}
                                className="w-16 px-2 py-1.5 rounded bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-meta text-gray-900 dark:text-white text-center focus:outline-none focus:border-primary/50" />
                              <select value={kr.status} onChange={(e) => updateKr(i, { status: e.target.value as KrStatus })}
                                className="flex-1 px-2 py-1.5 rounded bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-meta text-gray-900 dark:text-white focus:outline-none focus:border-primary/50">
                                {(Object.keys(KR_STATUS_LABELS) as KrStatus[]).map((s) => (
                                  <option key={s} value={s}>{KR_STATUS_LABELS[s]}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <button onClick={() => removeKr(i)} className="mt-1 text-gray-400 hover:text-red-500 transition-colors">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {saveMutation.isError && (
                    <p className="text-meta text-red-400">{(saveMutation.error as Error).message}</p>
                  )}
                  <div className="flex gap-2">
                    <button onClick={() => saveMutation.mutate()}
                      disabled={saveMutation.isPending}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white rounded-lg text-meta font-medium hover:bg-primary/90 disabled:opacity-50">
                      <Save className="h-4 w-4" />
                      {saveMutation.isPending ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={() => setEditing(false)} className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-white/10 text-meta text-gray-500 hover:text-gray-900 dark:hover:text-white">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function NewGoalForm({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ title: '', timeframe: '', description: '', ownerName: '', keyResults: [] as KeyResult[] });

  const mutation = useMutation({
    mutationFn: () => createGoal({
      title: form.title,
      description: form.description || null,
      timeframe: form.timeframe || null,
      ownerId: null,
      ownerName: form.ownerName || null,
      keyResults: form.keyResults,
    }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: queryKeys.goals }); onDone(); },
  });

  const addKr = () => setForm((p) => ({ ...p, keyResults: [...p.keyResults, emptyKr()] }));
  const removeKr = (i: number) => setForm((p) => ({ ...p, keyResults: p.keyResults.filter((_, idx) => idx !== i) }));
  const updateKr = (i: number, patch: Partial<KeyResult>) =>
    setForm((p) => ({ ...p, keyResults: p.keyResults.map((kr, idx) => idx === i ? { ...kr, ...patch } : kr) }));

  return (
    <div className="border border-primary/30 rounded-xl p-5 bg-primary/[0.02] space-y-4">
      <p className="text-title text-gray-900 dark:text-white">New Objective</p>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="block text-meta text-gray-500 mb-1">Objective title *</label>
          <input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} placeholder="e.g. Improve customer retention"
            className="w-full px-3 py-2 rounded-lg bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-body text-gray-900 dark:text-white focus:outline-none focus:border-primary/50" />
        </div>
        <div>
          <label className="block text-meta text-gray-500 mb-1">Timeframe</label>
          <input value={form.timeframe} onChange={(e) => setForm((p) => ({ ...p, timeframe: e.target.value }))} placeholder="e.g. Q2 2026"
            className="w-full px-3 py-2 rounded-lg bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-body text-gray-900 dark:text-white focus:outline-none focus:border-primary/50" />
        </div>
        <div>
          <label className="block text-meta text-gray-500 mb-1">Owner</label>
          <input value={form.ownerName} onChange={(e) => setForm((p) => ({ ...p, ownerName: e.target.value }))} placeholder="Owner name"
            className="w-full px-3 py-2 rounded-lg bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-body text-gray-900 dark:text-white focus:outline-none focus:border-primary/50" />
        </div>
        <div className="col-span-2">
          <label className="block text-meta text-gray-500 mb-1">Description</label>
          <textarea value={form.description} rows={2} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-body text-gray-900 dark:text-white focus:outline-none focus:border-primary/50 resize-none" />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-meta font-medium text-gray-700 dark:text-gray-300">Key Results</p>
          <button onClick={addKr} className="text-meta text-primary hover:text-primary/80 flex items-center gap-1">
            <Plus className="h-4 w-4" /> Add KR
          </button>
        </div>
        <div className="space-y-4">
          {form.keyResults.map((kr, i) => (
            <div key={i} className="flex items-start gap-2 bg-white dark:bg-white/[0.03] rounded-lg px-3 py-2 border border-gray-100 dark:border-white/[0.06]">
              <div className="flex-1 grid grid-cols-2 gap-2">
                <input value={kr.title} placeholder="Key result title"
                  onChange={(e) => updateKr(i, { title: e.target.value })}
                  className="col-span-2 px-2 py-1.5 rounded bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-meta text-gray-900 dark:text-white focus:outline-none focus:border-primary/50" />
                <input value={kr.metric ?? ''} placeholder="Metric (optional)"
                  onChange={(e) => updateKr(i, { metric: e.target.value || null })}
                  className="px-2 py-1.5 rounded bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-meta text-gray-900 dark:text-white focus:outline-none focus:border-primary/50" />
                <input type="number" min={0} max={100} value={kr.progress}
                  onChange={(e) => updateKr(i, { progress: Math.max(0, Math.min(100, parseInt(e.target.value) || 0)) })}
                  className="px-2 py-1.5 rounded bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-meta text-gray-900 dark:text-white text-center focus:outline-none focus:border-primary/50" />
              </div>
              <button onClick={() => removeKr(i)} className="mt-1 text-gray-400 hover:text-red-500"><X className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>

      {mutation.isError && <p className="text-meta text-red-400">{(mutation.error as Error).message}</p>}
      <div className="flex gap-2">
        <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
          onClick={() => mutation.mutate()} disabled={!form.title || mutation.isPending}
          className="flex items-center gap-1.5 px-4 py-2 bg-primary text-white rounded-xl text-meta font-medium hover:bg-primary/90 disabled:opacity-50">
          <Save className="h-4 w-4" />
          {mutation.isPending ? 'Creating...' : 'Create Objective'}
        </motion.button>
        <button onClick={onDone} className="px-4 py-2 rounded-xl border border-gray-200 dark:border-white/10 text-meta text-gray-500 hover:text-gray-900 dark:hover:text-white">
          Cancel
        </button>
      </div>
    </div>
  );
}

export function GoalSettings() {
  const { data: goals, isLoading } = useQuery({ queryKey: queryKeys.goals, queryFn: listGoals });
  const [adding, setAdding] = useState(false);

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => <div key={i} className="skeleton h-14 rounded-xl" />)}
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-4">
      <div>
        <h3 className="text-title text-gray-900 dark:text-white flex items-center gap-1.5">
          <Target className="h-4 w-4 text-primary" />
          Objectives & Key Results
        </h3>
        <p className="text-meta text-gray-500 dark:text-gray-400 mt-0.5">
          Define company objectives. Projects can be linked to key results from their dashboard.
        </p>
      </div>

      <div className="space-y-4">
        {goals?.map((goal) => (
          <GoalRow key={goal.goalId} goal={goal} onSaved={() => {}} />
        ))}
        {goals?.length === 0 && !adding && (
          <p className="text-body text-gray-400 dark:text-gray-500 italic py-4 text-center">
            No objectives yet. Create one to start tracking progress.
          </p>
        )}
      </div>

      {adding ? (
        <NewGoalForm onDone={() => setAdding(false)} />
      ) : (
        <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
          onClick={() => setAdding(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl border border-dashed border-gray-300 dark:border-white/20 text-meta text-gray-500 dark:text-gray-400 hover:border-primary/40 hover:text-primary transition-colors w-full justify-center">
          <Plus className="h-4 w-4" />
          Add Objective
        </motion.button>
      )}
    </div>
  );
}
